/**
 * Generates a device fingerprint from browser attributes.
 * This creates a unique identifier for the device that can be used
 * for the "remember this device" feature.
 *
 * The fingerprint combines:
 * - User agent string
 * - Screen resolution
 * - Timezone
 * - Browser language
 *
 * The result is hashed to create a consistent, privacy-friendly identifier.
 * Max length is 255 characters per API spec.
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.languages.join(','),
    navigator.hardwareConcurrency?.toString() ?? '',
    navigator.platform,
  ];

  const rawFingerprint = components.join('|');

  // Use SubtleCrypto to create a SHA-256 hash
  const encoder = new TextEncoder();
  const data = encoder.encode(rawFingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

  // Prefix with 'fp_' for easy identification, truncate to max 255 chars
  return `fp_${hashHex}`.slice(0, 255);
}
